import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import { ScanLine, RotateCw, Crop, Wand2, Trash2, Upload, Download, Save, Eye, AlertTriangle, UserPlus, Sparkles, Printer } from 'lucide-react';
import NewClientForm from '../components/NewClientForm';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Foto dei documenti scattate col telefono → PDF pulito a grandezza reale (backend:
// routes/scanner_documenti.py). `sigla` = prefisso con cui il documento viene salvato tra i
// "Documenti anagrafici" del cliente, lo stesso di DOC_LABEL_MAP in Dimissioni.js.
// `ocr` = document_type di /ocr/extract-client-data (stessi prompt di Dimissioni); la tessera
// sanitaria non ha un prompt di estrazione: il cliente si sceglie o si registra a mano.
const TIPI_DOCUMENTO = [
  { sigla: 'CIE', label: "Carta d'identità", tipo: 'id1', ocr: 'carta_identita', lati: ['Fronte', 'Retro'] },
  { sigla: 'PAT', label: 'Patente', tipo: 'id1', ocr: 'patente', lati: ['Fronte', 'Retro'] },
  { sigla: 'PSE', label: 'Permesso di soggiorno', tipo: 'id1', ocr: 'permesso_soggiorno', lati: ['Fronte', 'Retro'] },
  { sigla: 'TS', label: 'Tessera sanitaria', tipo: 'id1', ocr: null, lati: ['Fronte', 'Retro'] },
  { sigla: 'PASS', label: 'Passaporto', tipo: 'passaporto', ocr: 'passaporto', lati: ['Pagina dati', 'Altra pagina (facoltativa)'] },
  { sigla: 'LIB', label: 'Libretto di circolazione', tipo: 'foglio', ocr: null, lati: ['Pagina 1', 'Pagina 2 (facoltativa)'] },
];

// Lati elaborati (data URL) impilati in un'unica immagine JPEG per l'OCR: alla risoluzione
// piena dello scanner, invece del PDF A4 dove la tessera occupa un terzo del foglio.
const immagineUnica = (dataUrls) => new Promise((resolve, reject) => {
  Promise.all(dataUrls.map((u) => new Promise((ok, ko) => {
    const img = new Image();
    img.onload = () => ok(img);
    img.onerror = ko;
    img.src = u;
  }))).then((imgs) => {
    const spazio = 20;
    const canvas = document.createElement('canvas');
    canvas.width = Math.max(...imgs.map((i) => i.width));
    canvas.height = imgs.reduce((t, i) => t + i.height, 0) + spazio * (imgs.length - 1);
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#fff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    let y = 0;
    imgs.forEach((i) => { ctx.drawImage(i, 0, y); y += i.height + spazio; });
    canvas.toBlob((b) => (b ? resolve(b) : reject(new Error('canvas'))), 'image/jpeg', 0.92);
  }).catch(reject);
});

const latoVuoto = () => ({ file: null, url: '', risultato: null, loading: false });

// Editor manuale dei 4 angoli sulla foto originale: maniglie trascinabili (mouse o touch).
const EditorAngoli = ({ url, angoli, onChange }) => {
  const box = useRef(null);
  const [trascina, setTrascina] = useState(null);

  const posizione = (e) => {
    const r = box.current.getBoundingClientRect();
    return [
      Math.min(1, Math.max(0, (e.clientX - r.left) / r.width)),
      Math.min(1, Math.max(0, (e.clientY - r.top) / r.height)),
    ];
  };

  return (
    <div
      ref={box}
      className="relative inline-block select-none touch-none"
      onPointerMove={(e) => {
        if (trascina === null) return;
        const nuovi = angoli.map((p, i) => (i === trascina ? posizione(e) : p));
        onChange(nuovi);
      }}
      onPointerUp={() => setTrascina(null)}
      onPointerLeave={() => setTrascina(null)}
    >
      <img src={url} alt="Foto originale" className="block max-h-[65vh] max-w-full" draggable={false} />
      <svg className="absolute inset-0 w-full h-full" viewBox="0 0 1 1" preserveAspectRatio="none">
        <polygon
          points={angoli.map((p) => p.join(',')).join(' ')}
          fill="rgba(37, 99, 235, 0.15)"
          stroke="#2563eb"
          strokeWidth="0.004"
        />
      </svg>
      {angoli.map((p, i) => (
        <div
          key={i}
          onPointerDown={(e) => { e.preventDefault(); e.currentTarget.parentElement.setPointerCapture(e.pointerId); setTrascina(i); }}
          className="absolute w-6 h-6 -ml-3 -mt-3 rounded-full border-2 border-white bg-blue-600 shadow cursor-move"
          style={{ left: `${p[0] * 100}%`, top: `${p[1] * 100}%` }}
          title="Trascina sull'angolo del documento"
        />
      ))}
    </div>
  );
};

const ScannerDocumenti = () => {
  const [sigla, setSigla] = useState('CIE');
  const [lati, setLati] = useState([latoVuoto(), latoVuoto()]);
  const [editor, setEditor] = useState(null); // { indice, angoli }
  const [clients, setClients] = useState([]);
  const [clientSearch, setClientSearch] = useState('');
  const [showClientDropdown, setShowClientDropdown] = useState(false);
  const [cliente, setCliente] = useState(null);
  const [scadenza, setScadenza] = useState('');
  const [lavoro, setLavoro] = useState(false);
  const [estraendo, setEstraendo] = useState(false);
  const [nuovoCliente, setNuovoCliente] = useState(null); // null = chiuso, {} = vuoto, {...} = precompilato
  const ultimaEstrazione = useRef('');
  const [anteprimaPdf, setAnteprimaPdf] = useState(null); // { url, nome }

  const tipoDoc = TIPI_DOCUMENTO.find((t) => t.sigla === sigla);

  useEffect(() => {
    axios.get(`${API}/clients`)
      .then((res) => setClients(Array.isArray(res.data) ? res.data : (res.data.items || [])))
      .catch(() => toast.error('Errore nel caricamento dei clienti'));
  }, []);

  // libera gli URL delle foto quando la pagina si chiude
  const latiRef = useRef(lati);
  latiRef.current = lati;
  useEffect(() => () => latiRef.current.forEach((l) => l.url && URL.revokeObjectURL(l.url)), []);

  const aggiornaLato = (i, valori) => setLati((prev) => prev.map((l, j) => (j === i ? { ...l, ...valori } : l)));

  const elabora = async (i, file, opzioni = {}) => {
    aggiornaLato(i, { loading: true });
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('tipo', tipoDoc.tipo);
      if (opzioni.angoli) fd.append('angoli', JSON.stringify(opzioni.angoli));
      if (opzioni.rotazione !== undefined) fd.append('rotazione', String(opzioni.rotazione));
      // correzione manuale: il server la conserva come esempio per migliorare l'automatico
      if (opzioni.correzione) fd.append('correzione', opzioni.correzione);
      const res = await axios.post(`${API}/scanner-documenti/elabora`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      aggiornaLato(i, { risultato: res.data, loading: false });
      // passaporto caricato con il tipo di una tessera: il server lo riconosce dalle righe MRZ
      if (res.data.tipo === 'passaporto' && tipoDoc.tipo !== 'passaporto') {
        setSigla('PASS');
        toast.info('Riconosciuto un passaporto: tipo documento impostato su "Passaporto"');
      }
      if (!res.data.trovato) toast.warning('Bordi del documento non trovati: correggili a mano');
    } catch (error) {
      aggiornaLato(i, { loading: false });
      toast.error(error.response?.data?.detail || 'Errore durante l\'elaborazione della foto');
    }
  };

  const caricaFoto = (i, file) => {
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Carica una foto (JPG o PNG)');
      return;
    }
    if (lati[i].url) URL.revokeObjectURL(lati[i].url);
    aggiornaLato(i, { file, url: URL.createObjectURL(file), risultato: null });
    elabora(i, file);
  };

  const rimuovi = (i) => {
    if (lati[i].url) URL.revokeObjectURL(lati[i].url);
    aggiornaLato(i, latoVuoto());
  };

  const cambiaTipo = (nuova) => {
    const nuovoTipo = TIPI_DOCUMENTO.find((t) => t.sigla === nuova);
    setSigla(nuova);
    // il ritaglio dipende dalle proporzioni del documento: tessera ≠ passaporto
    if (nuovoTipo.tipo !== tipoDoc.tipo) {
      lati.forEach((l) => l.url && URL.revokeObjectURL(l.url));
      setLati([latoVuoto(), latoVuoto()]);
    }
  };

  const latiPronti = lati.filter((l) => l.risultato);

  const creaPdf = async () => {
    const fd = new FormData();
    fd.append('tipo', tipoDoc.tipo);
    fd.append('titolo', nomeDocumento());
    for (const [i, l] of latiPronti.entries()) {
      const blob = await (await fetch(l.risultato.immagine)).blob();
      fd.append('files', blob, `lato${i + 1}.jpg`);
    }
    const res = await axios.post(`${API}/scanner-documenti/pdf`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
      responseType: 'blob',
    });
    return new Blob([res.data], { type: 'application/pdf' });
  };

  // "CIE COGNOME NOME", come i documenti salvati dai Servizi: dal cliente scelto o, se non è
  // ancora registrato, dai dati letti dal documento
  const nomeDocumento = () => {
    const p = cliente || nuovoCliente || {};
    return `${sigla} ${p.last_name || ''} ${p.first_name || ''}`.replace(/\s+/g, ' ').trim().toUpperCase();
  };

  const scaricaBlob = (url, nome) => {
    const a = document.createElement('a');
    a.href = url;
    a.download = `${nome}.pdf`;
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  const conPdf = async (azione) => {
    if (!lati[0].risultato) {
      toast.error(`Carica almeno: ${tipoDoc.lati[0]}`);
      return;
    }
    setLavoro(true);
    try {
      await azione(await creaPdf());
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nella creazione del PDF');
    } finally {
      setLavoro(false);
    }
  };

  const chiudiAnteprima = () => {
    if (anteprimaPdf) URL.revokeObjectURL(anteprimaPdf.url);
    setAnteprimaPdf(null);
  };

  const anteprima = () => conPdf(async (pdf) => {
    setAnteprimaPdf({ url: URL.createObjectURL(pdf), nome: nomeDocumento() });
  });

  const scarica = () => conPdf(async (pdf) => {
    const url = URL.createObjectURL(pdf);
    scaricaBlob(url, nomeDocumento());
    setTimeout(() => URL.revokeObjectURL(url), 10000);
  });

  const salva = () => {
    if (!cliente) {
      toast.error('Seleziona il cliente');
      return;
    }
    conPdf(async (pdf) => {
      const nome = nomeDocumento();
      // stesso comportamento del caricamento da Dimissioni: un solo documento per tipo in anagrafica
      const res = await axios.get(`${API}/clients/${cliente.id}`);
      const esistente = (res.data.documents || []).find(
        (d) => d.category === 'anagrafico' && (d.description || '').toUpperCase().startsWith(`${sigla} `)
      );
      if (esistente && !window.confirm(`Il cliente ha già "${esistente.description}". Sostituirlo con questa scansione?`)) {
        return;
      }
      const fd = new FormData();
      fd.append('file', new File([pdf], `${nome}.pdf`, { type: 'application/pdf' }));
      fd.append('description', nome);
      fd.append('category', 'anagrafico');
      if (scadenza) fd.append('expiry_date', scadenza);
      await axios.post(`${API}/clients/${cliente.id}/documents`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      if (esistente) await axios.delete(`${API}/clients/${cliente.id}/documents/${esistente.id}`);
      toast.success(`Salvato in anagrafica: ${nome}`);
      lati.forEach((l) => l.url && URL.revokeObjectURL(l.url));
      setLati([latoVuoto(), latoVuoto()]);
      setScadenza('');
    });
  };

  const selezionaCliente = (c) => {
    setCliente(c);
    setClientSearch(`${c.last_name || ''} ${c.first_name || ''}`.trim());
    setNuovoCliente(null);
  };

  // Legge i dati dal documento (OCR locale, come in Dimissioni): se il codice fiscale è di un
  // cliente già in anagrafica lo seleziona, altrimenti apre la registrazione precompilata.
  const estraiDati = async () => {
    if (!tipoDoc.ocr || !latiPronti.length) return;
    setEstraendo(true);
    try {
      const fd = new FormData();
      fd.append('file', await immagineUnica(latiPronti.map((l) => l.risultato.immagine)), 'documento.jpg');
      fd.append('document_type', tipoDoc.ocr);
      const res = await axios.post(`${API}/ocr/extract-client-data`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      const d = res.data.extracted_data || {};
      const c = res.data.client_data || {};
      if (d.expiry_date) setScadenza(d.expiry_date);
      if (!c.fiscal_code && !c.last_name) {
        toast.error('Nessun dato riconosciuto: controlla il tipo documento o scegli il cliente a mano');
        return;
      }
      const cf = (c.fiscal_code || '').toUpperCase();
      const trovato = cf && clients.find((x) => (x.fiscal_code || '').toUpperCase() === cf);
      if (trovato) {
        selezionaCliente(trovato);
        toast.success(`Cliente già in anagrafica: ${trovato.last_name} ${trovato.first_name}`);
      } else {
        let dati = c;
        // CAP non stampato sulla CIE: se comune e provincia ne individuano uno solo, lo compila
        if (!dati.postal_code && dati.city && dati.city_provincia) {
          try {
            const capRes = await axios.get(`${API}/cap`, { params: { city: dati.city, provincia: dati.city_provincia } });
            if (capRes.data.single && capRes.data.auto_cap) dati = { ...dati, postal_code: capRes.data.auto_cap };
          } catch (_) {
            // il CAP è un aiuto in più, non blocca la registrazione
          }
        }
        setCliente(null);
        setNuovoCliente(dati);
        toast.info('Cliente non presente in anagrafica: controlla i dati e registralo');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Estrazione dati non riuscita: scegli o registra il cliente a mano');
    } finally {
      setEstraendo(false);
    }
  };

  // Estrazione automatica appena le foto sono pronte (una volta per ogni combinazione di lati)
  useEffect(() => {
    if (!tipoDoc.ocr || !lati[0].risultato || lati.some((l) => l.loading) || (lati[1].file && !lati[1].risultato)) return;
    const chiave = `${sigla}|${lati.map((l) => (l.file ? `${l.file.name}:${l.file.size}` : '')).join('|')}`;
    if (ultimaEstrazione.current === chiave) return;
    ultimaEstrazione.current = chiave;
    estraiDati();
  }, [lati, sigla]); // eslint-disable-line react-hooks/exhaustive-deps

  const filteredClients = clients.filter((c) => {
    if (!clientSearch) return true;
    const parole = clientSearch.toLowerCase().split(/\s+/).filter(Boolean);
    const testo = `${c.last_name || ''} ${c.first_name || ''} ${c.fiscal_code || ''}`.toLowerCase();
    return parole.every((p) => testo.includes(p));
  });

  return (
    <div className="space-y-6 animate-fade-in" data-testid="scanner-documenti-page">
      <div>
        <h1 className="text-3xl font-bold text-slate-900 tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
          Scanner Documenti
        </h1>
        <p className="text-slate-600 mt-1">
          Dalla foto del telefono al PDF pulito a grandezza reale: ritaglio, raddrizzamento e verso automatici
        </p>
      </div>

      <Card className="border-slate-200">
        <CardContent className="p-4 space-y-2 max-w-sm">
          <Label>Tipo documento</Label>
          <Select value={sigla} onValueChange={cambiaTipo}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              {TIPI_DOCUMENTO.map((t) => <SelectItem key={t.sigla} value={t.sigla}>{t.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {lati.map((l, i) => (
          <Card key={i} className="border-slate-200">
            <CardContent className="p-4 space-y-3">
              <div className="flex items-center justify-between">
                <h2 className="font-semibold text-slate-900">{tipoDoc.lati[i]}</h2>
                {l.risultato && <span className="text-xs text-slate-500">{l.risultato.motivo}</span>}
              </div>

              {!l.file ? (
                <label className="flex flex-col items-center justify-center gap-2 h-48 border-2 border-dashed border-slate-300 rounded-lg cursor-pointer hover:bg-slate-50 text-slate-500">
                  <Upload className="w-6 h-6" />
                  <span className="text-sm">Carica la foto</span>
                  <input
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    className="hidden"
                    onChange={(e) => { caricaFoto(i, e.target.files?.[0]); e.target.value = ''; }}
                  />
                </label>
              ) : (
                <>
                  <div className="flex items-center justify-center min-h-48 bg-slate-100 rounded-lg p-2">
                    {l.loading ? (
                      <span className="text-sm text-slate-500">Elaborazione...</span>
                    ) : l.risultato ? (
                      <img src={l.risultato.immagine} alt={tipoDoc.lati[i]} className="max-h-80 max-w-full shadow" />
                    ) : null}
                  </div>
                  {l.risultato && !l.risultato.trovato && (
                    <p className="text-sm text-amber-700 flex items-center gap-1">
                      <AlertTriangle className="w-4 h-4" /> Bordi non trovati: usa "Correggi bordi"
                    </p>
                  )}
                  <div className="flex flex-wrap gap-2">
                    <Button
                      type="button" variant="outline" size="sm" disabled={l.loading || !l.risultato}
                      onClick={() => elabora(i, l.file, { angoli: l.risultato.angoli, rotazione: (l.risultato.rotazione + (tipoDoc.tipo === 'id1' ? 180 : 90)) % 360, correzione: 'verso' })}
                    >
                      <RotateCw className="w-4 h-4 mr-1" /> Ruota
                    </Button>
                    <Button
                      type="button" variant="outline" size="sm" disabled={l.loading || !l.risultato}
                      onClick={() => setEditor({ indice: i, angoli: l.risultato.angoli })}
                    >
                      <Crop className="w-4 h-4 mr-1" /> Correggi bordi
                    </Button>
                    <Button type="button" variant="outline" size="sm" disabled={l.loading} onClick={() => elabora(i, l.file)}>
                      <Wand2 className="w-4 h-4 mr-1" /> Automatico
                    </Button>
                    <Button type="button" variant="ghost" size="sm" disabled={l.loading} onClick={() => rimuovi(i)} className="text-red-600">
                      <Trash2 className="w-4 h-4 mr-1" /> Rimuovi
                    </Button>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        ))}
      </div>

      <Card className="border-slate-200">
        <CardContent className="p-4 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-2 md:col-span-2 relative">
              <div className="flex items-center justify-between">
                <Label>Cliente</Label>
                <div className="flex gap-3">
                  {tipoDoc.ocr && (
                    <button type="button" className="text-sm text-blue-600 hover:underline flex items-center gap-1 disabled:opacity-50"
                      disabled={estraendo || !latiPronti.length} onClick={estraiDati}>
                      <Sparkles className="w-4 h-4" /> {estraendo ? 'Lettura dati...' : 'Estrai dati dal documento'}
                    </button>
                  )}
                  <button type="button" className="text-sm text-blue-600 hover:underline flex items-center gap-1"
                    onClick={() => { setCliente(null); setNuovoCliente({}); }}>
                    <UserPlus className="w-4 h-4" /> Nuovo cliente
                  </button>
                </div>
              </div>
              <Input
                placeholder="Cerca cliente per nome o codice fiscale..."
                value={clientSearch}
                onChange={(e) => { setClientSearch(e.target.value); setCliente(null); setShowClientDropdown(true); }}
                onFocus={() => setShowClientDropdown(true)}
                onBlur={() => setTimeout(() => setShowClientDropdown(false), 200)}
              />
              {showClientDropdown && clientSearch && (
                <div className="absolute z-50 top-full left-0 right-0 mt-1 border rounded-lg max-h-48 overflow-y-auto bg-white shadow-lg">
                  {filteredClients.length === 0 ? (
                    <div className="p-3 text-center text-slate-500 text-sm">Nessun cliente trovato</div>
                  ) : (
                    filteredClients.slice(0, 10).map((c) => (
                      <div
                        key={c.id}
                        onMouseDown={(e) => {
                          e.preventDefault();
                          selezionaCliente(c);
                          setShowClientDropdown(false);
                        }}
                        className="p-3 cursor-pointer hover:bg-slate-50 border-b last:border-b-0"
                      >
                        <div className="font-medium">{c.last_name} {c.first_name}</div>
                        <div className="text-sm text-slate-500 font-mono">{c.fiscal_code}</div>
                      </div>
                    ))
                  )}
                </div>
              )}
              {cliente && <p className="text-sm text-green-600">Verrà salvato come: {nomeDocumento()}</p>}
            </div>
            <div className="space-y-2">
              <Label>Scadenza documento</Label>
              <Input type="date" value={scadenza} onChange={(e) => setScadenza(e.target.value)} />
            </div>
          </div>
          {nuovoCliente && (
            <div className="border rounded-lg p-4 bg-slate-50">
              <NewClientForm
                key={JSON.stringify(nuovoCliente)}
                prefill={nuovoCliente}
                onCancel={() => setNuovoCliente(null)}
                onCreated={(creato) => {
                  setClients((prev) => [creato, ...prev]);
                  selezionaCliente(creato);
                }}
              />
            </div>
          )}
          <div className="flex flex-wrap gap-2">
            <Button type="button" variant="outline" disabled={lavoro || !latiPronti.length} onClick={anteprima}>
              <Eye className="w-4 h-4 mr-2" /> Anteprima PDF
            </Button>
            <Button type="button" variant="outline" disabled={lavoro || !latiPronti.length} onClick={scarica}>
              <Download className="w-4 h-4 mr-2" /> Scarica PDF
            </Button>
            <Button type="button" disabled={lavoro || !latiPronti.length || !cliente} onClick={salva}>
              <Save className="w-4 h-4 mr-2" /> {lavoro ? 'Attendere...' : 'Salva in anagrafica'}
            </Button>
          </div>
        </CardContent>
      </Card>

      <Dialog open={!!anteprimaPdf} onOpenChange={(open) => !open && chiudiAnteprima()}>
        <DialogContent className="max-w-5xl h-[90vh] flex flex-col p-0">
          <DialogHeader className="p-4 border-b">
            <div className="flex items-center justify-between gap-4 pr-8">
              <DialogTitle>{anteprimaPdf?.nome}</DialogTitle>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={() => {
                  const iframe = document.getElementById('scanner-anteprima-iframe');
                  try { iframe.contentWindow.print(); } catch (_e) { toast.error('Impossibile aprire il dialog di stampa'); }
                }}>
                  <Printer className="w-4 h-4 mr-1" /> Stampa
                </Button>
                <Button size="sm" variant="outline" onClick={() => scaricaBlob(anteprimaPdf.url, anteprimaPdf.nome)}>
                  <Download className="w-4 h-4 mr-1" /> Scarica
                </Button>
              </div>
            </div>
            <DialogDescription className="sr-only">Anteprima del PDF</DialogDescription>
          </DialogHeader>
          <div className="flex-1 min-h-0 bg-slate-100">
            {anteprimaPdf && (
              <iframe id="scanner-anteprima-iframe" title={anteprimaPdf.nome} src={anteprimaPdf.url} className="w-full h-full border-0" />
            )}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!editor} onOpenChange={(open) => !open && setEditor(null)}>
        <DialogContent className="max-w-4xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><ScanLine className="w-5 h-5" /> Correggi bordi</DialogTitle>
            <DialogDescription>Trascina i 4 punti blu sugli angoli del documento, poi premi Applica.</DialogDescription>
          </DialogHeader>
          {editor && (
            <div className="flex justify-center">
              <EditorAngoli
                url={lati[editor.indice].url}
                angoli={editor.angoli}
                onChange={(angoli) => setEditor({ ...editor, angoli })}
              />
            </div>
          )}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setEditor(null)}>Annulla</Button>
            <Button
              type="button"
              onClick={() => {
                const { indice, angoli } = editor;
                setEditor(null);
                elabora(indice, lati[indice].file, { angoli, correzione: 'bordi' });
              }}
            >
              Applica
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ScannerDocumenti;
